import { supabase } from '../supabaseClient';

/**
 * Enhanced fetch wrapper that handles authentication:
 * 1. Attaches Authorization header
 * 2. If 401, attempts to refresh session using Supabase
 * 3. Retries original request with new token
 * 
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function fetchWithAuth(url, options = {}) {
  // Helpers to get current token
  const getSession = async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session) return null;
    return session;
  };

  const getHeaders = (token, existingHeaders = {}) => ({
    ...existingHeaders,
    'Authorization': `Bearer ${token}`,
    // Ensure we keep other headers like Content-Type if provided
  });

  // 1. Initial Attempt
  let session = await getSession();
  let token = session?.access_token;

  // // If no session inititally, you might choose to throw or just proceed 
  // // (backend will 401). Let's let the backend decide or throw if strictly required.
  // if (!token) {
  //   console.warn("fetchWithAuth: No session found initially.");
  // }

  const initialOptions = {
    ...options,
    headers: getHeaders(token, options.headers),
  };

  let response = await fetch(url, initialOptions);

  // 2. Handle 401 - Token potentially expired
  if (response.status === 401) {
    console.log("fetchWithAuth: Received 401. Attempting to refresh session...");

    // Try to refresh the session via Supabase
    // refreshSession() returns { data: { session, user }, error }
    const { data, error: refreshError } = await supabase.auth.refreshSession();
    console.log("fetchWithAuth: Refresh session result:", data, refreshError);
    if (refreshError || !data.session) {
      console.error("fetchWithAuth: Failed to refresh session.", refreshError);
      
      // Clear anything if needed, or let component handle the final 401
      // If we cannot refresh, we just return the original 401 response
      // or we could throw an error. Returning original response lets caller handle redirect.
      return response;
    }

    // 3. Retry with new token
    console.log("fetchWithAuth: Session refreshed. Retrying request...");
    const newToken = data.session.access_token;
    console.log("New Token", newToken)
    const retryOptions = {
      ...options,
      headers: getHeaders(newToken, options.headers),
    };
    
    response = await fetch(url, retryOptions);
  }

  return response;
}
